import { IntlShape } from 'react-intl';
import { InfoItemProps } from '@designsystem/stories/02-atoms/Info';
import { TagProps } from '@designsystem/stories/02-atoms/Tag';
import {
  CardStyle,
  NewsContent,
  NormalizableContent,
  TypeNormalizer,
} from '../types';
import { stringOrUndefined } from '../utils';
import { messages } from '../messages';
import dateFormat from '@fhnw/components/DateFormat/dateFormat';

/**
 * Normalizes news content (FHNWNews)
 */
export const normalizeNewsCard: TypeNormalizer = (
  item,
  normalized,
  intl,
  university,
) => {
  const newsItem = item as NewsContent;
  const style: CardStyle = normalized?.style;

  // Set category from event type
  if (newsItem?.news_category) {
    normalized.category = stringOrUndefined(newsItem.news_category, intl);
  }

  if (style === 'tile' || style === 'list') {
    // Build tags
    const tags = buildNewsTags(newsItem, intl, university);

    if (tags.length > 0) {
      normalized.tags = tags;
    }
  } else {
    // Build info
    const info = buildNewsInfo(newsItem, intl, style, university);

    if (info.length > 0) {
      normalized.info = info;
    }
  }

  return normalized;
};

/**
 * Builds tags for news cards
 */
const buildNewsTags = (
  news: NewsContent,
  intl: IntlShape,
  university?: NormalizableContent,
): TagProps[] => {
  const tags: TagProps[] = [];

  if (news.effective || news.created) {
    tags.push({
      label: dateFormat({
        start: new Date((news.effective || news.created) as string),
        dateOnly: true,
        locale: intl.locale,
      }),
    });
  }

  if (university?.['@id'] && news?.organisation_entities) {
    const newsOEContent = Array.isArray(news.organisation_entities)
      ? news.organisation_entities
      : [news.organisation_entities];
    newsOEContent.forEach((oe) => {
      tags.push({
        label: stringOrUndefined(oe, intl) as string,
      });
    });
  }

  if (!university?.['@id'] && news?.university_tokens) {
    const newsUniversitiesContent = Array.isArray(news.university_tokens)
      ? news.university_tokens
      : [news.university_tokens];
    newsUniversitiesContent.forEach((u) => {
      tags.push({
        label: stringOrUndefined(u.title, intl) as string,
      });
    });
  }

  return tags;
};

/**
 * Builds info for news cards
 */
const buildNewsInfo = (
  news: NewsContent,
  intl: IntlShape,
  style: CardStyle,
  university?: NormalizableContent,
): InfoItemProps[] => {
  const info: InfoItemProps[] = [];

  if (style === 'table' && news?.news_category) {
    info.push({
      key: intl.formatMessage(messages.Newstype),
      value: stringOrUndefined(news.news_category, intl),
    });
  }

  if (news.effective || news.created) {
    info.push({
      key: intl.formatMessage(messages.Date),
      value: dateFormat({
        start: new Date((news.effective || news.created) as string),
        dateOnly: true,
        locale: intl.locale,
      }),
    });
  }

  if (
    university?.['@id'] &&
    news?.organisation_entities &&
    news.organisation_entities?.length > 0
  ) {
    info.push({
      key: intl.formatMessage(messages.Institution),
      value: stringOrUndefined(news.organisation_entities, intl),
    });
  }

  if (!university?.['@id'] && news?.university_tokens) {
    const newsUniversitiesContent = stringOrUndefined(
      (Array.isArray(news.university_tokens)
        ? news.university_tokens
        : [news.university_tokens]
      )?.map((u) => u.title),
      intl,
    );
    if (newsUniversitiesContent) {
      info.push({
        key: intl.formatMessage(messages.School),
        value: newsUniversitiesContent,
      });
    }
  }

  return info;
};
